import AdvancedDataTable from 'Components/Generic/AdvancedDataTable';
import DataTableHeaderTextFieldFilter from 'Components/Generic/AdvancedDataTable/DataTableHeaderTextFieldFilter';
import { TEXT_FILTER_MAP } from 'Components/Generic/AdvancedDataTable/Helper';
import OrganizationMultiselect from 'Components/Generic/BOSMultiselect/OrganizationMultiselect';
import ResourceMultiselect from 'Components/Generic/BOSMultiselect/ResourceMultiselect';
import VendorMultiselect from 'Components/Generic/BOSMultiselect/VendorMultiselect';
import BOSSelect from 'Components/Generic/BOSSelect';
import QuickViewButton from 'Components/Generic/Buttons/QuickViewButton';
import StatusButton from 'Components/Generic/Buttons/StatusButton';
import OrgNameWithDrawerButton from 'Components/SiteDrawerContainer/OrgNameWithDrawerButton';
import ResourceDrawerButton from 'Components/SiteDrawerContainer/ResourceDrawerButton';
import ServiceContractDrawerButton from 'Components/SiteDrawerContainer/ServiceContractDrawerButton';
import VendorNameWithDrawerButton from 'Components/SiteDrawerContainer/VendorNameWithDrawerButton';
import { getVendorNameFromId } from 'Data/context/Vendors';
import { getObjectStatus, OBJECT_STATUS, user_roles } from 'Data/Enum';
import ServiceContractData from 'Data/ServiceContractData';
import ServiceContractDetailsData from 'Data/ServiceContractDetailsData';
import SiteDrawerData from 'Data/SiteDrawerData';
import DataManager from 'Lib/DataManager';
import { usePersistentFiltering } from 'Lib/hooks/usePersistentFiltering';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { isUserAuthorized } from 'Util';
import { formatDate } from 'Util/dateUtil';

let DM = new DataManager();

let filterOptions = {
  name: {
    seqRestKey: '*name',
    mapToSeqRestValue: TEXT_FILTER_MAP,
  },
  organization: {
    seqRestKey: '*$service_contract_details.organization_id',
  },
  asset: {
    seqRestKey: '*$service_contract_details.resource_id',
  },
  vendor: {
    seqRestKey: '*vendor_id',
  },
  user_id: {
    seqRestKey: '*user_id',
  },
  status: {
    seqRestKey: '*status',
  },
};

const ServiceContractListView = ({ addNewButton }) => {
  const navigate = useNavigate();
  const [data, setData] = useState();
  const [refreshKey, setRefreshKey] = useState(0);
  const [persistentFilter, updateFilterField] = usePersistentFiltering(
    `SC_FILTER`,
    { status: [OBJECT_STATUS.ACTIVE] },
  );

  useEffect(() => {
    DM.add(
      ServiceContractData.registerListener('LIST_LOADED', setData),
      ServiceContractData,
    );
    DM.add(
      ServiceContractData.registerListener(
        'LIST_LOAD_ERROR',
        onServiceContractListLoadError,
      ),
      ServiceContractData,
    );
    DM.add(
      ServiceContractData.registerListener('RECORD_CREATED', refreshData),
      ServiceContractData,
    );
    DM.add(
      ServiceContractData.registerListener(
        'RECORD_UPDATED',
        refreshAndCloseDrawer,
      ),
      ServiceContractData,
    );
    DM.add(
      ServiceContractData.registerListener(
        'RECORD_ACTIVATED',
        refreshAndCloseDrawer,
      ),
      ServiceContractData,
    );
    DM.add(
      ServiceContractData.registerListener(
        'RECORD_DEACTIVATED',
        refreshAndCloseDrawer,
      ),
      ServiceContractData,
    );
    DM.add(
      ServiceContractDetailsData.registerListener(
        'RECORD_CREATED',
        refreshData,
      ),
      ServiceContractData,
    );
    DM.add(
      ServiceContractDetailsData.registerListener(
        'RECORDS_DELETED',
        refreshData,
      ),
      ServiceContractData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  const onServiceContractListLoadError = () => {};

  const refreshData = () => {
    setRefreshKey((prevState) => prevState + 1);
  };
  const refreshAndCloseDrawer = () => {
    refreshData();
    SiteDrawerData.close();
  };

  const headers = [
    {
      label: 'Vendor',
      value: 'vendor',
      fieldName: 'vendor_id',
      sortable: true,
      sortFieldName: 'vendors.name',
      filterable: true,
      renderHeader() {
        return (
          <VendorMultiselect
            onSelectedVendorsUpdate={(vendorList) =>
              updateFilterField('vendor', vendorList)
            }
            defaultSelectedItems={persistentFilter?.vendor}
            isHeader
          />
        );
      },
      format: (vendor_id) => {
        return <VendorNameWithDrawerButton vendorId={vendor_id} />;
      },
      getText: (order) => {
        return getVendorNameFromId(order.vendor_id);
      },
    },

    {
      label: 'Name',
      value: 'name',
      sortable: true,
      filterable: true,
      renderHeader() {
        return (
          <DataTableHeaderTextFieldFilter
            fieldName={'Name'}
            defaultValue={persistentFilter?.name}
            onFilterValueUpdate={(e) =>
              updateFilterField('name', e.target.value)
            }
          />
        );
      },
      render(serviceContract) {
        return isUserAuthorized({
          roles: [user_roles.SC_ADMIN],
        }) ? (
          <Link to={`./${serviceContract.id}`}>{serviceContract.name}</Link>
        ) : (
          serviceContract.name
        );
      },
    },

    {
      label: 'Assets',
      value: 'service_contract_details_assets',
      fieldName: 'service_contract_details',
      filterable: true,
      renderHeader() {
        return (
          <ResourceMultiselect
            labelText={'Assets'}
            onSelectedResourcesUpdate={(resourceList) =>
              updateFilterField('asset', resourceList)
            }
            defaultSelectedItems={persistentFilter?.asset}
            isHeader
          />
        );
      },
      format: (details) => {
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              paddingTop: 4,
              paddingBottom: 4,
            }}>
            {details
              .map((detail) => detail.resource)
              .filter((resource) => !!resource)
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((resource, index) => (
                <ResourceDrawerButton
                  key={index}
                  label={resource.name}
                  resourceId={resource.id}
                />
              ))}
          </div>
        );
      },
      getText: (sc) =>
        sc.service_contract_details
          ? sc.service_contract_details
              .map((detail) => detail.resource)
              .filter((resource) => !!resource)
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((org) => org.name)
              .join(', ')
          : '',
    },

    {
      label: 'Core Facility',
      value: 'service_contract_details',
      fieldName: 'service_contract_details',
      filterable: true,
      renderHeader() {
        return (
          <OrganizationMultiselect
            onSelectedOrganizationsUpdate={(orgList) =>
              updateFilterField('organization', orgList)
            }
            defaultSelectedItems={persistentFilter?.organization}
            isHeader
          />
        );
      },
      format: (details) => {
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              paddingTop: 4,
              paddingBottom: 4,
            }}>
            {details
              .map((detail) => detail.organization)
              .filter(
                (obj1, i, arr) =>
                  arr.findIndex((obj2) => obj2.id === obj1.id) === i,
              )
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((org, index) => (
                <OrgNameWithDrawerButton key={index} orgId={org.id} />
              ))}
          </div>
        );
      },
      getText: (sc) =>
        sc.service_contract_details
          ? sc.service_contract_details
              .map((detail) => detail.organization)
              .filter(
                (obj1, i, arr) =>
                  arr.findIndex((obj2) => obj2.id === obj1.id) === i,
              )
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((org) => org.name)
              .join(', ')
          : '',
    },

    // {
    //   label: 'User',
    //   value: 'user_id',
    //   width: 160,
    //   filterable: true,
    //   renderHeader() {
    //     return (
    //       <UserMultiselect
    //         labelText='User'
    //         onSelectedUsersUpdate={(userList) =>
    //           updateFilterField('user_id', userList)
    //         }
    //         defaultSelectedItems={persistentFilter?.user_id}
    //       />
    //     );
    //   },
    //   format: (user_id) => {
    //     return <UserNameWithDrawerButton userId={user_id} />;
    //   },
    //   getText: (sc) => {
    //     return getUserNameFromId(sc.user_id);
    //   },
    // },

    {
      label: 'Start Date',
      value: 'start_date',
      width: 100,
      format: (start_date) => formatDate(start_date),
      getText: (sc) => formatDate(sc.start_date),
    },

    {
      label: 'End Date',
      value: 'end_date',
      width: 100,
      format: (end_date) => formatDate(end_date),
      getText: (sc) => formatDate(sc.end_date),
    },

    {
      label: 'Status',
      value: 'status',
      width: 120,
      centeredHeader: true,
      filterable: true,
      renderHeader() {
        return (
          <BOSSelect
            listData={Object.values(OBJECT_STATUS)}
            idField={'key'}
            labelField={'label'}
            labelText='Status'
            onItemSelect={(item) => updateFilterField('status', [item])}
            defaultSelectedId={
              persistentFilter?.status?.length
                ? persistentFilter.status[0].key
                : undefined
            }
            isHeader
          />
        );
      },
      format: (status) => {
        return <StatusButton statusKey={status} />;
      },
      getText: (sc) => getObjectStatus(sc.status)?.label || sc.status,
    },

    {
      label: '',
      value: 'quick_view',
      isIconCol: true,
      render(sc) {
        return (
          <ServiceContractDrawerButton
            editable={isUserAuthorized({
              roles: [user_roles.SC_ADMIN],
            })}
            serviceContractId={sc.id}
            label={<QuickViewButton />}
            drawerTitle={sc.name}
            linkToDetailId={sc.id}
          />
        );
      },
    },
  ];

  return (
    <AdvancedDataTable
      headers={headers}
      refreshKey={refreshKey}
      title={'Manage Service Contracts'}
      dataKey={'SC'}
      data={data}
      dataQueryFunc={ServiceContractData.getServiceContractList}
      filterOptions={filterOptions}
      filterBy={persistentFilter}
      updateFilterField={updateFilterField}
      defaultSort={'vendors.name'}
      actionButtons={addNewButton}
      exportDataFunc={ServiceContractData.exportContractList}
      navigateToDetailsFunc={(record) => {
        navigate(`./${record.id}`);
      }}
      adjustRowHeightToFit
    />
  );
};

ServiceContractListView.propTypes = {
  addNewButton: PropTypes.node,
};

export default ServiceContractListView;
